import { EventAsyncGS, EventAsyncGSToken } from '@ivanlog/gs';
import { ethers } from 'ethers';
import { App } from '../App';
import { BiMath } from '../libs/BiMath';
import { LPXContract, LPXReserves } from './LPXContract';
import { LPXCtrlPoolMngrContract } from './LPXCtrlPoolMngrContract';
import { LPXCtrlPoolsContract } from './LPXCtrlPoolsContract';
import { LPXLibProfitContract } from './LPXLibProfitContract';
import { ChainProvider } from './abstact/ContractFacade';
import { ERC20Helper } from './ERC20Helper';
import { WrappedETHContract } from './WrappedETH';
import { AuthRequest, LPXInfo, LPXSearchRequest } from '../api/classes/models';
import { EIP712AuthSigner } from '../classes/EIP712AuthSigner';
import { PulseChain } from './PulseChain';

type ConfigPack = {
    feeIndex: bigint;
    ntzIndex: bigint;
    profitIndex: bigint;
    managerRewardIndex: bigint;
    poolType: number;
    hasPoolFlag_ManagerOnly: boolean;
};

type SolveReservesResult = {
    fundImgReserve: bigint;
    anchorImgReserve: bigint;
    iterations: number;
    actualEndRatio: bigint;
};

type CheckResult = {
    reserves: LPXReserves;
    error: string;
    endPrice: bigint;
};

export class LPXInfrastructure {
    public static ADDRESS_LPX: string = '0x43993C4faA1bE0915A03a3DCF88223D4c1897Cb1';
    public static ADDRESS_LPXCtrlPools: string = '0x072aC94d110088beb0b9905417f9Fca40FD005A0';
    public static ADDRESS_LPXCtrlPoolMngr: string = '0x74761eA6b79EF9e453B86f6AC2d83337C7BaE08E';
    public static ADDRESS_LPXLibArbProfit: string = '0x6e718Cfde0659B8331f17A43D4773b3094671655';
    public static ADDRESS_WPLS: string = '0xA1077a294dDE1B09bB078844df40758a5D0f9a27';
    public static ADDRESS_ERC20Helper: string = '0x9fa22DC4b8879642fbE82F2CA433724d6975B8f4';
    public static ADDRESS_DEBUG: string = '0x07B2F1Ed9B644dB28Ad7051bEF2bA51d443a8b4C';
    public static POOLTYPE_DUAL: number = 1;
    public static POOLTYPE_DIRECTION: number = 2;
    public static DUAL_NTZ_MIN_INDEX: bigint = 200n;
    public static DUAL_FEE_MIN_INDEX: bigint = 24n;
    public static DUAL_FEE_FROM_NTZ_INDEX: bigint = 12000n;
    public static PACKFLAG_MANAGER_ONLY: number = 128;
    public static PACKFLAG_MANAGER_SET_MANAGER: number = 129;
    public static PACKFLAG_MANAGER_SET_POOL_INDEXES: number = 130;
    public static PACKFLAG_MANAGER_SCALE_POOL_LIQUIDITY: number = 131;
    public static PACKFLAG_POOL_PAUSE_SWAP: number = 192;
    public static PACKFLAG_POOL_PAUSE_SWAP_ADDLIQ: number = 193;
    public static PACKFLAG_POOL_PAUSE_AUTO_PRICE_DOWN: number = 194;
    public static PACKFLAG_POOL_OBLIVION: number = 195;
    public static SID_STORAGE_KEY: string = 'lpx.auth.sidByAccount.v1';

    private _lpx: LPXContract;
    private _lpxCtrlPools: LPXCtrlPoolsContract;
    private _lpxCtrlPoolMngr: LPXCtrlPoolMngrContract;
    private _lpxLibArbProfit: LPXLibProfitContract;
    private _wpls: WrappedETHContract;
    private _erc20Helper: ERC20Helper;

    private _auths: Map<string, string> = new Map<string, string>();
    //private _symbols: Map<string, string> = new Map<string, string>();
    //private _decimals: Map<string, bigint> = new Map<string, bigint>();
    //private _apr: Map<number, number> = new Map<number, number>();

    private _onRefresh: EventAsyncGS;
    private _onRefreshToken: EventAsyncGSToken<void>;

    private _normalizeTokenKey(token: string): string { return (token ?? '').toLowerCase(); }

    constructor() {
        this._onRefreshToken = new EventAsyncGSToken<void>();
        this._onRefresh = new EventAsyncGS(this._onRefreshToken);

        //this._symbols.set(this._normalizeTokenKey('0x57fde0a71132198BBeC939B98976993d8D89D225'), 'eHEX');
        // this._decimals.set(this._normalizeTokenKey('0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39'), BigInt(100000000));
        // this._decimals.set(this._normalizeTokenKey('0x57fde0a71132198BBeC939B98976993d8D89D225'), BigInt(100000000));
        // this._decimals.set(this._normalizeTokenKey('0x3819f64f282bf135d62168C1e513280dAF905e06'), BigInt(1000000000));
        // this._decimals.set(this._normalizeTokenKey('0xfc4913214444aF5c715cc9F7b52655e788A569ed'), BigInt(1000000000));
        // this._decimals.set(this._normalizeTokenKey('0xb17D901469B9208B17d916112988A3FeD19b5cA1'), BigInt(100000000));
    }

    get lpx() { return this._lpx; }
    get lpxCtrlPools() { return this._lpxCtrlPools; }
    get lpxCtrlPoolMngr() { return this._lpxCtrlPoolMngr; }
    get lpxLibArbProfit() { return this._lpxLibArbProfit; }
    get wpls() { return this._wpls; }
    get erc20Helper() { return this._erc20Helper; }
    get onRefresh() { return this._onRefresh; }

    public hasAuthState(account = App.chain.account): boolean
    {
        if (!account) return false;
        return this._auths.has(account.toLowerCase());
    }

    public dropAuthState(account = App.chain.account)
    {
        if (!account) return;
        const accountKey = account.toLowerCase();
        if (!this._auths.delete(accountKey)) return;
        this._saveAuths();
    }




    async initializeAsync(provider: ChainProvider): Promise<boolean> {
        try {
            try {
                this._auths.clear();
                const raw = globalThis.localStorage?.getItem(LPXInfrastructure.SID_STORAGE_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw) as Record<string, string>;
                    for (const account of Object.keys(parsed))
                        if (parsed[account]) this._auths.set(account.toLowerCase(), parsed[account]);
                }
            } catch (storageError) {
                console.error(storageError);
            }
            this._lpx = new LPXContract(LPXInfrastructure.ADDRESS_LPX, provider);
            this._lpxCtrlPools = new LPXCtrlPoolsContract(LPXInfrastructure.ADDRESS_LPXCtrlPools, provider);
            this._lpxCtrlPoolMngr = new LPXCtrlPoolMngrContract(LPXInfrastructure.ADDRESS_LPXCtrlPoolMngr, provider);
            this._lpxLibArbProfit = new LPXLibProfitContract(LPXInfrastructure.ADDRESS_LPXLibArbProfit, provider);
            this._wpls = new WrappedETHContract(LPXInfrastructure.ADDRESS_WPLS, provider);
            this._erc20Helper = new ERC20Helper(LPXInfrastructure.ADDRESS_ERC20Helper, provider);
            App.chain.addContracts([this._lpx, this._lpxCtrlPools, this._lpxCtrlPoolMngr, this._lpxLibArbProfit, this._wpls, this._erc20Helper]);
            return true;
        } catch (e) {
            App.gui.dialogOkShowAsync(e);
        }
        return false;
    }

    public async authorizeAsync(): Promise<boolean>
    {
        const account = App.chain.account;
        if (!account) return false;
        const accountKey = account.toLowerCase();
        if (this._auths.has(accountKey)) return true;

        const modal = await App.gui.dialogOkNoShowAsync("Sign a read-only message to load your Solo LPX list. Not a transaction. No assets will be moved."); //TODO
        if (!modal) return false;
        const domainName = "LPX";
        const domainVersion = "v1";
        const verifyingContract = LPXInfrastructure.ADDRESS_LPX;
        try {
            const signer = App.chain.provider?.signer;
            if (!signer) return false;

            const chainId = await App.chain.getChainIdAsync();
            if (chainId === undefined) return false;

            const sid = await App.api.authService.getSid({ skip: true });
            if (!sid) return false;

            const signature = await EIP712AuthSigner.signLogin(signer, {
                sid: sid,
                walletAddress: account,
                domainName: domainName,
                domainVersion: domainVersion,
                chainId: chainId,
                verifyingContract: verifyingContract
            });

            const req = new AuthRequest();
            req.sid = sid;
            req.signature = signature;
            req.address = account;
            req.domainName = domainName;
            req.domainVersion = domainVersion;
            req.chainId = chainId;
            req.verifyingContract = verifyingContract;

            const authResult = await App.api.authService.auth(req, { skip: true });
            if (!authResult) return false;
            this._auths.set(accountKey, sid);
            this._saveAuths();
            return true;
        } catch (e) {
            console.error(e);
            return false;
        }
    }

    public async getLPXsAsync(skip=0,take=15, searchQuery="", filterPortfolio=false, filterManager=false, filterSolo=false):Promise<LPXInfo[]|undefined>
    {
        const req = new LPXSearchRequest();
        const account = App.chain.account??"";   
        const accountKey = account.toLowerCase();
        req.forWalletAdr = account;
        req.forToken = searchQuery;
        req.skip=skip;
        req.take=take;
        req.isFilterPorfolio=filterPortfolio;
        req.isFilterManager=filterManager;
        req.isFilterOnlySolo = filterSolo;
        req.sid= "";
        const sid = this._auths.get(accountKey);
        if (filterSolo&&!sid) return [];
        if (sid) req.sid = sid;
       
        let lpx: LPXInfo[]|undefined;
        try {
            const result = await App.api.lPXWatcher.searchLPXsUnsafe(req);
            if (Array.isArray(result)) lpx = result;
            else throw result;
        } catch (e:any) {
            const code = e?.code;
            const status = e?.status;
            if ((code==401||status==401)&&sid)
            {
                this.dropAuthState(account);
                if (filterSolo) return [];

                // Retry public search once without the stale SID.
                req.sid = "";
                try {
                    const retryResult = await App.api.lPXWatcher.searchLPXsUnsafe(req);
                    if (Array.isArray(retryResult)) lpx = retryResult;
                    else throw retryResult;
                } catch (retryError) {
                    console.error(retryError);
                }
            } else console.error(e);
        }
        if (lpx) //заменяем символ для коррекции
            for(const p of lpx)
            {
                let t = p.fundToken;
                //if (!this._decimals.has(t.erc20Address)) this._decimals.set(this._normalizeTokenKey(t.erc20Address), BiMath.Pow10(t.decimals));
                t.symbol = PulseChain.GetSymbolOrDef(t.erc20Address,t.symbol);
                t = p.anchorToken;
                //if (!this._decimals.has(t.erc20Address)) this._decimals.set(this._normalizeTokenKey(t.erc20Address), BiMath.Pow10(t.decimals));
                t.symbol = PulseChain.GetSymbolOrDef(t.erc20Address,t.symbol);
            }
        return lpx;
    }

    async refreshAsync() { await this._onRefreshToken.invoke(); }

    private _saveAuths()
    {
        try {
            if (this._auths.size==0) { globalThis.localStorage?.removeItem(LPXInfrastructure.SID_STORAGE_KEY); return; }
            const authObject: Record<string, string> = {};
            for (const [adr, savedSid] of this._auths) authObject[adr] = savedSid;
            globalThis.localStorage?.setItem(LPXInfrastructure.SID_STORAGE_KEY, JSON.stringify(authObject));
        } catch (storageError) {
            console.error(storageError);
        }
    }

    // public getApr(poolId: number) {
    //     const result = this._apr.get(poolId);
    //     if (result === undefined) return '-';
    //     return parseInt((result * 1000) + '') / 10 + '%';
    // }

    public async getSymbolsAsync(tokens: string[]): Promise<Map<string, string>> {
        // Batch-resolve symbols with deduplication:
        // same token in different address casing is merged to one RPC request.
        const result = new Map<string, string>();
        const tokenToRefresh: string[] = [];
        const aliases = new Map<string, string[]>();

        for (const token of tokens) {
            const tokenKey = this._normalizeTokenKey(token);
            if (tokenKey === ethers.ZeroAddress.toLowerCase()) continue;

            const cached = PulseChain.GetSymbol(token);
            if (cached !== undefined) {
                result.set(token, cached);
                continue;
            }

            const linked = aliases.get(tokenKey);
            if (linked) linked.push(token);
            else {
                aliases.set(tokenKey, [token]);
                tokenToRefresh.push(tokenKey);
            }
        }

        if (tokenToRefresh.length > 0) {
            const symbols = await this.erc20Helper.getSymbols(tokenToRefresh);
            for (let i = 0; i < tokenToRefresh.length; i++) {
                const tokenKey = tokenToRefresh[i];
                const symbol = symbols[i];
                PulseChain.SetSymbol(tokenKey, symbol);
                for (const token of aliases.get(tokenKey) ?? []) result.set(token, symbol);
            }
        }

        return result;
    }

    public async getSymbolAsync(token: string): Promise<string> {
        const symbol = PulseChain.GetSymbol(token);
        if (symbol) return symbol;
        const symbols = await this.erc20Helper.getSymbols([token]);
        PulseChain.SetSymbol(token, symbols[0]);
        return symbols[0];
    }

    // public getDecimal(token: string): bigint {
    //     const tokenKey = this._normalizeTokenKey(token);
    //     if (this._decimals.has(tokenKey)) return this._decimals.get(tokenKey);
    //     return BiMath.Decimal;
    // }

    // public getDecimalCorIndex(token: string): bigint {
    //     // Correction coefficient to normalize token amount to BiMath.Decimal domain.
    //     // Integer math only (bigint), so fractional remainder is truncated.
    //     const decimal = this.getDecimal(token);
    //     return BiMath.Decimal / decimal * BiMath.Decimal;
    // }

    // public getDecimalCorPairIndex(tokenF: string, tokenA: string): bigint {
    //     // Pair correction between two token decimal domains.
    //     // Keeps arithmetic in bigint-space, so tiny fractions are floored.
    //     const dF = this.getDecimal(tokenF);
    //     const dA = this.getDecimal(tokenA);
    //     if (dF == dA) return BiMath.Decimal;
    //     if (dF < dA) return (dF * BiMath.Decimal) / dA;
    //     return dF / dA * BiMath.Decimal;
    // }

    public pAdrToAdr(poolAdr: bigint): string {
        const upper = poolAdr >> BigInt(160);
        if (upper !== BigInt(0)) throw new Error('LPX: no ERC20 adr');

        const lower160 = poolAdr & ((BigInt(1) << BigInt(160)) - BigInt(1));
        const hex = lower160.toString(16).padStart(40, '0');
        return ethers.getAddress('0x' + hex);
    }

    public adrToPAdr(address: string): bigint {
        const checksummed = ethers.getAddress(address);
        const value = BigInt(checksummed);

        const max160 = (BigInt(1) << BigInt(160)) - BigInt(1);
        if (value > max160) throw new Error('LPX: address exceeds 160 bits');

        return value;
    }

    public getUint17FromUint256(value: bigint, index: number): bigint {
        if (index < 0 || index > 14) throw new Error('Out of range index for 17-bit extract');
        const shift = BigInt(index) * BigInt(17);
        return (value >> shift) & BigInt(0x1FFFF);
    }

    public unpackConfigPack(pack: bigint): ConfigPack {
        // Layout is tightly coupled with on-chain bit packing:
        // [17 fee][17 ntz][17 profit][17 managerReward][4 poolType][flags...]
        return {
            feeIndex: this.getUint17FromUint256(pack, 0),
            ntzIndex: this.getUint17FromUint256(pack, 1),
            profitIndex: this.getUint17FromUint256(pack, 2),
            managerRewardIndex: this.getUint17FromUint256(pack, 3),
            poolType: Number(pack >> (BigInt(17) * BigInt(4)) & BigInt(0x0F)),
            hasPoolFlag_ManagerOnly: this.hasFlag(pack, LPXInfrastructure.PACKFLAG_MANAGER_ONLY)
        };
    }

    public calcCurrentRatio(reserve: LPXReserves): bigint {
        const numerator = reserve.anchorReserve + reserve.anchorImgReserve;
        const denominator = reserve.fundReserve + reserve.fundImgReserve;
        if (denominator === BiMath.Zero) throw new Error('Division by zero');
        return numerator * BiMath.Decimal / denominator;
    }

    public calcStartRatio(reserve: LPXReserves, fee17B: bigint): bigint {
        // Start ratio projection with fee applied in 17-bit decimal domain.
        if (reserve.anchorImgReserve === BiMath.Zero) throw new Error('LPX: anchorImgReserve=0');

        const totalY = reserve.fundReserve + reserve.fundImgReserve;
        const deltaY = reserve.anchorReserve * totalY / reserve.anchorImgReserve;
        const fundReserveEnd = reserve.fundReserve + deltaY * (BiMath.Decimal17B - fee17B) / BiMath.Decimal17B;

        const denominator = fundReserveEnd + reserve.fundImgReserve;
        const numerator = reserve.anchorImgReserve;
        return numerator * BiMath.Decimal/denominator ;
    }

    public calcEndRatio(reserve: LPXReserves, fee17B: bigint): bigint {
        // End ratio projection for the opposite side of reserves with same fee model.
        if (reserve.fundImgReserve === BiMath.Zero) throw new Error('LPX: fundImgReserve=0');

        const totalY = reserve.anchorReserve + reserve.anchorImgReserve;
        const deltaY = reserve.fundReserve * totalY / reserve.fundImgReserve;
        const anchorReserveEnd = reserve.anchorReserve + deltaY * (BiMath.Decimal17B - fee17B) / BiMath.Decimal17B;

        const numerator = anchorReserveEnd + reserve.anchorImgReserve;
        const denominator = reserve.fundImgReserve;
        return numerator * BiMath.Decimal / denominator;
    }

    public hasFlag(pack: bigint, pos: number): boolean { return (pack & (1n << BigInt(pos))) !== 0n; }

    public setFlag(pack: bigint, pos: number | bigint): bigint {
        const p = typeof pos === 'number' ? BigInt(pos) : pos;
        return pack | (BigInt(1) << p);
    }

    public check(
        fundReserve: bigint,
        anchorReserve: bigint,
        price: bigint,
        endPrice: bigint,
        fee: bigint,
        currentRatioNumerator?: bigint,
        currentRatioDenominator?: bigint
    ): CheckResult {
        // High-level safety check:
        // 1) solve synthetic reserves for target price corridor
        // 2) simulate end price
        // 3) enforce max 2% deviation policy
        const reserves = new LPXReserves();
        let actualEndPrice = endPrice;

        try {
            if (fundReserve <= 0n) throw new Error('Start Fund Amount must be greater than zero.');
            if (anchorReserve < 0n) throw new Error('Start Anchor Amount cannot be negative.');
            if (price <= 0n) throw new Error('Sell Price must be greater than zero.');
            if (endPrice <= 0n) throw new Error('Max Sell Price must be greater than zero.');
            if (fee < 0n || fee > BiMath.Decimal17B) throw new Error('fee out of range');
            if ((currentRatioNumerator === undefined) != (currentRatioDenominator === undefined))
                throw new Error('Exact Sell Price ratio must include numerator and denominator.');
            if (currentRatioNumerator !== undefined && currentRatioDenominator !== undefined)
            {
                if (currentRatioNumerator <= 0n || currentRatioDenominator <= 0n) throw new Error('Sell Price must be greater than zero.');
                if (currentRatioNumerator * BiMath.Decimal > endPrice * currentRatioDenominator)
                    throw new Error('Sell Price>Max Sell Price. Max Sell Price should be above Sell Price.');
            }

            let solveEndPrice = endPrice;
            let bestRes:SolveReservesResult|undefined;
            let bestActualEndPrice = actualEndPrice;
            let bestDiff:bigint|undefined;

            for (let i = 0; i < 8; i++)
            {
                const res = this._solveReserves(
                    fundReserve,
                    anchorReserve,
                    price,
                    solveEndPrice,
                    fee,
                    currentRatioNumerator,
                    currentRatioDenominator
                );
                const checkReserves = new LPXReserves();
                checkReserves.fundReserve = fundReserve;
                checkReserves.anchorReserve = anchorReserve;
                checkReserves.fundImgReserve = res.fundImgReserve;
                checkReserves.anchorImgReserve = res.anchorImgReserve;
                const simulatedEndPrice = this._simulateEndPrice(checkReserves, fee);
                const diff = simulatedEndPrice > endPrice ? simulatedEndPrice - endPrice : endPrice - simulatedEndPrice;
                if (bestDiff === undefined || diff < bestDiff)
                {
                    bestDiff = diff;
                    bestRes = res;
                    bestActualEndPrice = simulatedEndPrice;
                }

                let minPrice = simulatedEndPrice;
                let maxPrice = endPrice;
                if (minPrice > maxPrice)
                {
                    const t = minPrice;
                    minPrice = maxPrice;
                    maxPrice = t;
                }
                if (maxPrice > 0n && minPrice * 100n / maxPrice >= 98n) break;

                const previousSolveEndPrice = solveEndPrice;
                if (simulatedEndPrice < endPrice)
                {
                    if (simulatedEndPrice == 0n) solveEndPrice = previousSolveEndPrice + 1n;
                    else solveEndPrice = (solveEndPrice * endPrice + simulatedEndPrice - 1n) / simulatedEndPrice;
                    if (solveEndPrice <= previousSolveEndPrice) solveEndPrice = previousSolveEndPrice + 1n;
                } else
                {
                    if (simulatedEndPrice == 0n) solveEndPrice = previousSolveEndPrice + 1n;
                    else solveEndPrice = solveEndPrice * endPrice / simulatedEndPrice;
                    if (solveEndPrice >= previousSolveEndPrice && previousSolveEndPrice > price) solveEndPrice = previousSolveEndPrice - 1n;
                    if (solveEndPrice <= price) solveEndPrice = price + 1n;
                }
            }

            if (!bestRes) throw new Error('Not converged');

            reserves.fundReserve = fundReserve;
            reserves.anchorReserve = anchorReserve;
            reserves.fundImgReserve = bestRes.fundImgReserve;
            reserves.anchorImgReserve = bestRes.anchorImgReserve;
            actualEndPrice = bestActualEndPrice;

            let minPrice = actualEndPrice;
            let maxPrice = endPrice;
            if (minPrice > maxPrice)
            {
                const t = minPrice;
                minPrice = maxPrice;
                maxPrice = t;
            }

            const delta = minPrice * 100n / maxPrice;
            if (delta < 98n)
                throw new Error(
                    'Published Max Sell Price deviation is more than 2%.\n' +
                    'Adjust Max Sell Price, Sell Price, or Start Fund Amount.\n'
                );

            return { reserves, error: '', endPrice: actualEndPrice };
        } catch (error: any) {
            let message = error?.message + '';
            if (!message) message = 'unknown';
            return { reserves, error: message, endPrice: actualEndPrice };
        }
    }

    private _solveReserves(
        fundReserve: bigint,
        anchorReserve: bigint,
        currentRatio: bigint,
        endRatio: bigint,
        fee: bigint,
        currentRatioNumerator?: bigint,
        currentRatioDenominator?: bigint,
        maxIter = 10000
    ): SolveReservesResult {
        // Iterative solver with adaptive step and direction switch.
        // Designed for bigint arithmetic where each division floors the result.
        const DECIMAL = 10n ** 18n;
        const DECIMAL17B = 100000n;

        if (fundReserve <= 0n) throw new Error('Start Fund Amount must be greater than zero.');
        if (anchorReserve < 0n) throw new Error('Start Anchor Amount cannot be negative.');
        if (currentRatio <= 0n) throw new Error('Sell Price must be greater than zero.');
        if (endRatio <= 0n) throw new Error('Max Sell Price must be greater than zero.');
        if (fee < 0n || fee > BiMath.Decimal17B) throw new Error('fee out of range');
        if (currentRatio > endRatio) throw new Error('Sell Price>Max Sell Price. Max Sell Price should be above Sell Price.');
        if ((currentRatioNumerator === undefined) != (currentRatioDenominator === undefined))
            throw new Error('Exact Sell Price ratio must include numerator and denominator.');

        const ratioNumerator = currentRatioNumerator ?? currentRatio;
        const ratioDenominator = currentRatioDenominator ?? DECIMAL;
        if (ratioNumerator <= 0n || ratioDenominator <= 0n) throw new Error('Sell Price must be greater than zero.');

        let fundImgReserve = fundReserve;
        let anchorImgReserve = this._calcAnchorImgReserveForRatio(fundReserve, anchorReserve, fundImgReserve, ratioNumerator, ratioDenominator);

        let step = fundImgReserve;
        let dirUp = true;
        let bestFundImgReserve = fundImgReserve;
        let bestAnchorImgReserve = anchorImgReserve;
        let bestDiff:bigint|undefined = undefined;
        let bestRatio = 0n;
        const tolerance = endRatio < 50n ? 0n : endRatio / 10000n;

        for (let i = 0; i < maxIter; i++) {
            const totalY = anchorReserve + anchorImgReserve;
            const deltaY = fundReserve * totalY / fundImgReserve;
            const anchorReserveEnd = anchorReserve + deltaY * (DECIMAL17B - fee) / DECIMAL17B;
            const ratio = (anchorReserveEnd + anchorImgReserve) * DECIMAL / fundImgReserve;

            const diff = ratio > endRatio ? ratio - endRatio : endRatio - ratio;
            if ((bestDiff === undefined || diff < bestDiff) && fundImgReserve >= 0n && anchorImgReserve >= 0n)
            {
                bestDiff = diff;
                bestFundImgReserve = fundImgReserve;
                bestAnchorImgReserve = anchorImgReserve;
                bestRatio = ratio;
            }

            if (diff <= tolerance || step == 0n) {
                if (anchorImgReserve < 0n) {
                    anchorImgReserve = 0n;
                    fundImgReserve = (anchorReserve * ratioDenominator + ratioNumerator - 1n) / ratioNumerator - fundReserve;
                }
                if (fundImgReserve < 0n || anchorImgReserve < 0n)
                {
                    if (bestDiff === undefined) throw new Error('Not converged');
                    fundImgReserve = bestFundImgReserve;
                    anchorImgReserve = bestAnchorImgReserve;
                }
                let actualRatio = ratio;
                if (diff > tolerance && bestDiff !== undefined)
                {
                    fundImgReserve = bestFundImgReserve;
                    anchorImgReserve = bestAnchorImgReserve;
                    actualRatio = bestRatio;
                }

                return {
                    fundImgReserve,
                    anchorImgReserve,
                    iterations: i,
                    actualEndRatio: actualRatio
                };
            }

            if (step >= fundImgReserve / 6n) step = step / 3n;
            if (dirUp) fundImgReserve += step;
            else fundImgReserve -= step;

            anchorImgReserve = this._calcAnchorImgReserveForRatio(fundReserve, anchorReserve, fundImgReserve, ratioNumerator, ratioDenominator);
            if (ratio < endRatio && dirUp) {
                dirUp = false;
                step = step / 3n;
            }
            if (ratio > endRatio && !dirUp) {
                dirUp = true;
                step = step / 3n;
            }
        }

        throw new Error('Not converged');
    }

    private _calcAnchorImgReserveForRatio(
        fundReserve: bigint,
        anchorReserve: bigint,
        fundImgReserve: bigint,
        ratioNumerator: bigint,
        ratioDenominator: bigint
    ) {
        const fundTotal = fundReserve + fundImgReserve;
        const anchorTotal = (fundTotal * ratioNumerator + ratioDenominator - 1n) / ratioDenominator;
        return anchorTotal - anchorReserve;
    }

    private _simulateEndPrice(reserves: LPXReserves, feeRatio: bigint) {
        // Discrete simulation of reserve depletion:
        // step = 0.001% of initial fund reserve, clamped to min 1.
        const DECIMAL = 10n ** 18n;
        const DECIMAL17B = 100000n;

        let fundReserve = reserves.fundReserve;
        const fundImgReserve = reserves.fundImgReserve;
        let anchorReserve = reserves.anchorReserve;
        const anchorImgReserve = reserves.anchorImgReserve;

        if (fundImgReserve === 0n) throw new Error('fundImgReserve must be > 0');
        if (feeRatio < 0n || feeRatio > DECIMAL17B) throw new Error('feeRatio out of range');

        const initialFund = fundReserve;
        let step = initialFund / 100_000n;
        if (step < 1n) step = 1n;

        while (fundReserve > 0n) {
            const dx = fundReserve < step ? fundReserve : step;
            const totalY = anchorReserve + anchorImgReserve;
            const dy = dx * totalY / (fundReserve + fundImgReserve);
            const netDy = dy * (DECIMAL17B - feeRatio) / DECIMAL17B;

            anchorReserve += netDy;
            fundReserve -= dx;
        }

        return (anchorReserve + anchorImgReserve) * DECIMAL / fundImgReserve;
    }
}
